import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, X, Star } from "lucide-react";
import { api, resolveImg } from "@/lib/api";
import { ImageUpload } from "@/components/admin/ImageUpload";

const empty = { name: "", location: "", rating: 5, text: "", avatar: "", order: 0 };

export const ReviewsManager = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/reviews");
      setReviews(res.data);
    } catch (e) {
      toast.error("Kon reviews niet laden");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ ...empty, order: reviews.length }); setEditing("new"); };
  const openEdit = (r) => { setForm(r); setEditing(r.id); };
  const close = () => { setEditing(null); setForm(empty); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.name || !form.text) { toast.error("Naam en tekst zijn verplicht"); return; }
    setSaving(true);
    try {
      if (editing === "new") { await api.post("/admin/reviews", form); toast.success("Review toegevoegd"); }
      else { await api.put(`/admin/reviews/${editing}`, form); toast.success("Review bijgewerkt"); }
      close(); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Opslaan mislukt");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Deze review verwijderen?")) return;
    await api.delete(`/admin/reviews/${id}`);
    toast.success("Review verwijderd");
    load();
  };

  if (loading) return <div className="text-white/40 flex items-center gap-2"><Loader2 className="animate-spin" size={18} /> Laden...</div>;

  return (
    <div data-testid="reviews-manager">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-2xl font-bold text-white">Reviews ({reviews.length})</h2>
        <button onClick={openNew} data-testid="add-review-button" className="bg-[#FF1A1A] text-white px-5 py-2.5 font-semibold flex items-center gap-2 hover:bg-[#E60000] transition-colors">
          <Plus size={18} /> Nieuwe review
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {reviews.map((r) => (
          <div key={r.id} data-testid={`admin-review-${r.id}`} className="bg-[#111] border border-white/10 p-5">
            <div className="flex items-center gap-3">
              {r.avatar ? <img src={resolveImg(r.avatar)} alt="" className="w-10 h-10 rounded-full object-cover" /> : <div className="w-10 h-10 rounded-full bg-[#FF1A1A]/20 flex items-center justify-center text-[#FF1A1A] font-bold">{r.name[0]}</div>}
              <div>
                <p className="text-white font-semibold text-sm">{r.name}</p>
                <p className="text-white/40 text-xs">{r.location}</p>
              </div>
            </div>
            <div className="flex gap-0.5 mt-3">{[...Array(r.rating)].map((_, i) => <Star key={i} size={13} className="fill-yellow-400 text-yellow-400" />)}</div>
            <p className="text-white/50 text-xs italic mt-2 line-clamp-3">"{r.text}"</p>
            <div className="flex gap-2 mt-4">
              <button onClick={() => openEdit(r)} data-testid={`edit-review-${r.id}`} className="text-white/60 hover:text-white flex items-center gap-1 text-xs border border-white/15 px-3 py-1.5"><Pencil size={13} /> Bewerk</button>
              <button onClick={() => remove(r.id)} data-testid={`delete-review-${r.id}`} className="text-white/60 hover:text-[#FF1A1A] flex items-center gap-1 text-xs border border-white/15 px-3 py-1.5"><Trash2 size={13} /> Verwijder</button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center overflow-y-auto py-10 px-4">
          <form onSubmit={save} data-testid="review-form" className="bg-[#111] border border-white/10 w-full max-w-lg p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-xl font-bold text-white">{editing === "new" ? "Nieuwe review" : "Review bewerken"}</h3>
              <button type="button" onClick={close}><X className="text-white/50 hover:text-white" /></button>
            </div>
            <div className="flex flex-col gap-5">
              <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Naam" data-testid="review-name-input" className="w-full bg-transparent border-b border-white/20 focus:border-[#FF1A1A] outline-none py-2.5 text-white placeholder:text-white/30" />
              <input value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Plaats" data-testid="review-location-input" className="w-full bg-transparent border-b border-white/20 focus:border-[#FF1A1A] outline-none py-2.5 text-white placeholder:text-white/30" />
              <div>
                <label className="text-white/60 text-xs uppercase tracking-widest block mb-2">Sterren</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} type="button" onClick={() => set("rating", n)} data-testid={`review-star-${n}`}>
                      <Star size={26} className={n <= form.rating ? "fill-yellow-400 text-yellow-400" : "text-white/20"} />
                    </button>
                  ))}
                </div>
              </div>
              <textarea value={form.text} onChange={(e) => set("text", e.target.value)} placeholder="Review tekst" rows={4} data-testid="review-text-input" className="w-full bg-transparent border-b border-white/20 focus:border-[#FF1A1A] outline-none py-2.5 text-white placeholder:text-white/30 resize-none" />
              <ImageUpload label="Profielfoto (optioneel)" value={form.avatar} onChange={(v) => set("avatar", v)} testid="review-avatar-upload" />
              <button type="submit" disabled={saving} data-testid="save-review-button" className="bg-[#FF1A1A] text-white px-8 py-3.5 font-semibold hover:bg-[#E60000] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {saving ? <><Loader2 size={18} className="animate-spin" /> Opslaan...</> : "Opslaan"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ReviewsManager;
